import { useCallback, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router"

/**
 * The tab set shared by the manage template/schema detail panels (Product,
 * Host, Transaction Templates, Customer settings). Kept module-level so the
 * reference is stable across renders.
 */
export const MANAGE_TEMPLATE_TABS = [
  "general",
  "registration",
  "display",
  "actions",
] as const

/**
 * Syncs a Tabs component's active tab with the URL hash.
 *
 * A link like `/manage/products/templates#display` focuses the Display tab on
 * render, and switching tabs rewrites the hash in place (via `replace`, so it
 * never pollutes the back button). This lets the config-help "?" deep-link to a
 * specific tab on pages whose tabs are otherwise in-memory only.
 *
 * Pass the allowed tab values so an unknown/stale hash falls back to
 * `defaultTab` instead of selecting a non-existent tab. The default tab is
 * represented as an empty hash to keep the canonical URL clean.
 *
 * @returns `[value, onValueChange]` to spread onto `<Tabs value onValueChange>`.
 */
export function useHashTab(
  tabs: readonly string[],
  defaultTab: string,
): [string, (value: string) => void] {
  const location = useLocation()
  const navigate = useNavigate()

  const fromHash = useCallback(() => {
    const hash = location.hash.replace(/^#/, "")
    return tabs.includes(hash) ? hash : defaultTab
  }, [location.hash, tabs, defaultTab])

  const [tab, setTab] = useState(fromHash)

  // Follow later in-app hash changes (e.g. a second config-help link to the
  // same page targeting a different tab) after the initial mount.
  useEffect(() => {
    const next = fromHash()
    setTab((prev) => (prev === next ? prev : next))
  }, [fromHash])

  const onValueChange = useCallback(
    (value: string) => {
      setTab(value)
      const hash = value === defaultTab ? "" : `#${value}`
      navigate(`${location.pathname}${location.search}${hash}`, {
        replace: true,
      })
    },
    [navigate, location.pathname, location.search, defaultTab],
  )

  return [tab, onValueChange]
}
